import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Settings2, Plus, Trash2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TYPES = ["small", "big", "pickup", "walmart", "other"];

export default function RulesDialog({ trigger }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);

  const { data, refetch } = useQuery({
    queryKey: ["rules"],
    queryFn: async () => (await api.get("/rules")).data,
    enabled: open,
  });

  useEffect(() => {
    if (data?.rules) setRules(data.rules.map((r) => ({ ...r, prefixes: [...(r.prefixes || [])] })));
  }, [data]);

  function addRule() {
    setRules((rs) => [...rs, { type: "other", prefixes: [""] }]);
  }
  function removeRule(idx) {
    setRules((rs) => rs.filter((_, i) => i !== idx));
  }
  function setRuleType(idx, type) {
    setRules((rs) => rs.map((r, i) => i === idx ? { ...r, type } : r));
  }
  function setRulePrefixes(idx, csv) {
    const arr = csv.split(",").map((s) => s.trim()).filter(Boolean);
    setRules((rs) => rs.map((r, i) => i === idx ? { ...r, prefixes: arr } : r));
  }

  async function save() {
    setSaving(true);
    try {
      await api.post("/rules", { rules });
      toast.success("Rules saved");
      qc.invalidateQueries();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  }

  async function reset() {
    setSaving(true);
    try {
      const { data } = await api.post("/rules/reset");
      setRules(data.rules.map((r) => ({ ...r, prefixes: [...r.prefixes] })));
      await refetch();
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="open-rules" className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#E2E2E8] rounded-md hover:bg-slate-50">
            <Settings2 className="w-4 h-4" /> Edit rules
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Classification rules</DialogTitle>
          <DialogDescription>
            When you paste a roster, each route code is checked against these prefixes to pick its type. Add or remove rules as your fleet changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {rules.map((r, idx) => (
            <div key={idx} className="p-3 border border-[#E2E2E8] rounded-md" data-testid={`rule-${idx}`}>
              <div className="flex items-center gap-2">
                <select
                  value={r.type}
                  onChange={(e) => setRuleType(idx, e.target.value)}
                  data-testid={`rule-type-${idx}`}
                  className="px-3 py-2 border border-[#E2E2E8] rounded-md text-sm bg-white"
                >
                  {TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
                <span className="text-xs text-slate-500">starts with</span>
                <input
                  value={(r.prefixes || []).join(", ")}
                  onChange={(e) => setRulePrefixes(idx, e.target.value)}
                  placeholder="scar14, scar15"
                  data-testid={`rule-prefixes-${idx}`}
                  className="flex-1 px-3 py-2 border border-[#E2E2E8] rounded-md text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => removeRule(idx)}
                  data-testid={`rule-remove-${idx}`}
                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-1 text-xs text-slate-500">Comma-separated prefixes. Case-insensitive.</div>
            </div>
          ))}
          <button
            onClick={addRule}
            data-testid="rule-add"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm border border-dashed border-[#E2E2E8] rounded-md hover:bg-slate-50 text-slate-600"
          >
            <Plus className="w-4 h-4" /> Add rule
          </button>
        </div>

        <DialogFooter>
          <button onClick={reset} disabled={saving} data-testid="rules-reset" className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#E2E2E8] rounded-md hover:bg-slate-50 disabled:opacity-50">
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
          <button onClick={save} disabled={saving} data-testid="rules-save" className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save rules"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
